import { useCallback, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import type { Autocomplete } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import { Box, Text } from "@similarweb/ui-components/foundations";

import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";
import { swSettings } from "app/common/services/swSettings";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { setChatText } from "app/pages/sales-intelligence/modules/ai-agent/store";
import { SuperAgentButton } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentButton/SuperAgentButton";
import { useRecentAISearches } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/queries/useRecentAISearches";
import { StyledAutocompleteLowBannerWrapper } from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocomplete/styles";
import CompanyAutocompleteItem from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteItem/CompanyAutocompleteItem";
import CompanyAutocompleteUpgradeBlock from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteUpgradeBlock/CompanyAutocompleteUpgradeBlock";
import { AnimatedPlaceholder } from "app/pages/sales-intelligence/modules/company-autocomplete/components/HomePageAutocomplete/AnimatedPlaceholder/AnimatedPlaceholder";
import { AutocompleteAIItem } from "app/pages/sales-intelligence/modules/company-autocomplete/components/HomePageAutocomplete/AutocompleteAIItem";
import { EmptyState } from "app/pages/sales-intelligence/modules/company-autocomplete/components/HomePageAutocomplete/EmptyState";
import useCompanyAutocompleteCustomFetch from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteCustomFetch";
import { LowCreditsBanner } from "app/pages/sales-intelligence/modules/quota/components/LowCreditsBanner/LowCreditsBanner";
import { noTouchLowCreditsBannerConfigs } from "app/pages/sales-intelligence/modules/quota/constants/configuration";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { useLowCreditsBannerVisibility } from "app/pages/sales-intelligence/modules/quota/hooks/useLowCreditsBannerVisibility";
import { useLowQuotaBannerManagement } from "app/pages/sales-intelligence/modules/quota/hooks/useLowQuotaBannerManagement";
import { useIsCompanyLockedByQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useIsCompanyLockedByQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { AI_AGENT_MAX_OPTION_VALUE, AI_AGENT_PRICE_PER_RECORD } from "../../../quota/constants";
import { useQuotaGuard } from "../../../quota/hooks/useQuotaGuard";

import { MAX_RECENTS_AI_SUGGESTIONS_TO_SHOW, MAX_RESULTS_QUOTA_REACHED, MAX_RESULTS } from "./constants";
import { StyledCompanyAutocomplete, StyledItemsContainer } from "./styles";

const HomePageAutocomplete = ({ onItemClick }: { onItemClick: (domain: string, isCompanyLevel: boolean) => void }) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [isFocused, setIsFocused] = useState(false);

    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const autocompleteRef = useRef<Autocomplete>(null);

    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const { data: quota, isQuotaUnlimited, isQuotaReached } = useCompanyQuota();
    const isCompanyLocked = useIsCompanyLockedByQuota();
    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);
    const { getListItems, isLoading } = useCompanyAutocompleteCustomFetch(
        isQuotaReached ? MAX_RESULTS_QUOTA_REACHED : MAX_RESULTS,
        isDataCreditsEnabled,
    );
    const { data: { items: recentAISearches = [] } = {} } = useRecentAISearches();

    const getRecentAISearches = useCallback(
        (term: string) => {
            if (!term || !salesSettingsHelper.isSuperAgentEnabled) {
                return [];
            }
            return (
                recentAISearches.filter((recent) =>
                    recent?.threadSubject?.toLowerCase().includes(term.toLowerCase()),
                ) ?? []
            );
        },
        [recentAISearches],
    );

    const handleGetListItems = async (query: string): Promise<IAutoCompleteSiteItem[]> => {
        const term = query.trim();
        if (term.length === 0) {
            return [];
        }
        const data = await getListItems(term);
        scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" });

        return data;
    };

    const handleRecentAISearchClick = (id: string, text: string) => {
        AIAgentTrackingService.superAgent.trackRecentAISearchClick(text);
        void siNavigator.goToSuperAgentPage({ id });
    };

    const handleSiteItemClick = (item: IAutoCompleteSiteItem, domain: string, isCompanyLevel: boolean) => {
        if (isCompanyLocked(item.isPurchased)) {
            return;
        }
        onItemClick(domain, isCompanyLevel);
    };

    const showBanner = useLowCreditsBannerVisibility({
        totalQuota: quota?.total || 0,
        remainingQuota: quota?.remaining || 0,
        pricePerRecord: AI_AGENT_PRICE_PER_RECORD,
        maxOptionValue: AI_AGENT_MAX_OPTION_VALUE,
    });
    const { lowCreditsBannerConfigs: trialBannerConfigs } = useLowQuotaBannerManagement();
    const bannerConfigs = isTraditionalTrialUser(swSettings) ? trialBannerConfigs : noTouchLowCreditsBannerConfigs;

    const shouldShowLowCreditsBanner = useCallback(
        (listItems: IAutoCompleteSiteItem[]) =>
            isDataCreditsEnabled && listItems.length !== 0 && showBanner && !isLoading,
        [isDataCreditsEnabled, isLoading, showBanner],
    );

    const renderLowCreditsBanner = useCallback(
        (listItems: IAutoCompleteSiteItem[]) => {
            if (shouldShowLowCreditsBanner(listItems)) {
                return (
                    <StyledAutocompleteLowBannerWrapper>
                        <LowCreditsBanner {...bannerConfigs} />
                    </StyledAutocompleteLowBannerWrapper>
                );
            }
            return null;
        },
        [shouldShowLowCreditsBanner, bannerConfigs],
    );

    const isQuotaReachedWithoutDataCredits = useMemo(
        () => !isDataCreditsEnabled && isQuotaReached,
        [isDataCreditsEnabled, isQuotaReached],
    );

    const renderItems = ({ listItems, query }: { listItems: IAutoCompleteSiteItem[]; query: string }) => {
        if (!isFocused) {
            return null;
        }

        const term = query ? query.trim() : "";

        if (term.length === 0) {
            return (
                <EmptyState
                    onItemClick={onItemClick}
                    recentAISearches={recentAISearches.slice(0, MAX_RECENTS_AI_SUGGESTIONS_TO_SHOW)}
                    children={renderLowCreditsBanner(listItems)}
                />
            );
        }

        const filteredRecentAISearches = getRecentAISearches(term);
        const isSearchResults = listItems?.length || filteredRecentAISearches?.length;

        if (isSearchResults) {
            const cleanedTerm = term.toLowerCase().replace(/,/g, " ");

            return (
                <div className="ListItemsContainer">
                    <StyledItemsContainer ref={scrollAreaRef}>
                        {filteredRecentAISearches?.length
                            ? filteredRecentAISearches.map((recent) => (
                                  <AutocompleteAIItem
                                      key={recent.threadId}
                                      text={recent.threadSubject}
                                      searchTerm={cleanedTerm}
                                      onClick={() => handleRecentAISearchClick(recent.threadId, recent.threadSubject)}
                                  />
                              ))
                            : null}
                        {listItems?.length
                            ? listItems.map((company) => (
                                  <CompanyAutocompleteItem
                                      key={company.name}
                                      company={company}
                                      queryWords={cleanedTerm.split(" ").filter(Boolean)}
                                      onCompanyClick={() =>
                                          handleSiteItemClick(company, company.companyInfo?.mainDomain ?? "", true)
                                      }
                                      onWebsiteClick={(domain) => handleSiteItemClick(company, domain, false)}
                                      isLocked={isCompanyLocked(company.isPurchased)}
                                  />
                              ))
                            : null}
                        {renderLowCreditsBanner(listItems)}
                        {isQuotaReachedWithoutDataCredits ? (
                            <CompanyAutocompleteUpgradeBlock interval={quota?.interval} />
                        ) : null}
                    </StyledItemsContainer>
                </div>
            );
        }

        if (shouldShowLowCreditsBanner(listItems)) {
            return (
                <div className="ListItemsContainer">
                    <StyledItemsContainer>{renderLowCreditsBanner(listItems)}</StyledItemsContainer>
                </div>
            );
        }

        if (isQuotaReachedWithoutDataCredits) {
            return (
                <div className="ListItemsContainer">
                    <CompanyAutocompleteUpgradeBlock interval={quota?.interval} />
                </div>
            );
        }

        return null;
    };

    const getTextFieldValue = (): string => {
        // @ts-expect-error
        return autocompleteRef.current?.textFieldRef.getValue() ?? "";
    };

    const handleAIButtonClick = () => {
        const textFieldValue = getTextFieldValue();

        if (textFieldValue.length && !isBlocked) {
            dispatch(setChatText(textFieldValue));
            AIAgentTrackingService.superAgent.sendPromptFromHomePage(textFieldValue);
            void siNavigator.goToSuperAgentPage({ autogenerate: true });
            return;
        }

        void siNavigator.goToSuperAgentPage();
    };

    const handleEnterKeyPress = () => {
        if (salesSettingsHelper.isSuperAgentEnabled) {
            handleAIButtonClick();
        }
    };

    const isQuotaRenderedInInput = !isQuotaUnlimited && !isLoading && isFocused;

    return (
        <Box
            position="relative"
            margin="20px 0 0 0"
        >
            <StyledCompanyAutocomplete
                ref={autocompleteRef}
                placeholder={
                    salesSettingsHelper.isSuperAgentEnabled ? (
                        <AnimatedPlaceholder />
                    ) : (
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[300]}
                            display="block"
                            margin="0 0 0 36px"
                            dangerouslySetInnerHTML={{
                                __html: translate("si.pages.home.search.autocomplete.placeholder.company.text"),
                            }}
                        />
                    )
                }
                loadingComponent={<DotsLoader />}
                debounce={250}
                getListItems={handleGetListItems}
                renderItems={renderItems}
                isLoading={isLoading}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                overrideEnterFunc={handleEnterKeyPress}
                isFocused={isFocused}
                hasSuperAgentButton={salesSettingsHelper.isSuperAgentEnabled}
                isQuotaRenderedInInput={isQuotaRenderedInInput}
            />
            {!isDataCreditsEnabled && isQuotaRenderedInInput ? (
                <Box
                    position="absolute"
                    top="17px"
                    right={`${salesSettingsHelper.isSuperAgentEnabled ? "150px" : "16px"}`}
                    zIndex={1}
                >
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.midnight[100]}
                    >
                        {`${quota?.used}/${quota?.total}`} {translate("si.pages.home.search.autocomplete.quota")}
                    </Text>
                </Box>
            ) : null}
            {salesSettingsHelper.isSuperAgentEnabled ? (
                <Box
                    position="absolute"
                    top="7px"
                    right="6px"
                    zIndex={1}
                >
                    <SuperAgentButton onClick={handleAIButtonClick} />
                </Box>
            ) : null}
        </Box>
    );
};

export default HomePageAutocomplete;
